/**
 * hq-combo-logic
 *
 * The rules behind the bundles + closure set (snippets/hq-combo-builder.liquid,
 * assets/hq-combo-builder.js), kept free of the DOM so they can be tested in
 * Node (theme-tests/tests/combo-logic.test.js).
 *
 * The set is real products: N bundles of one listing (the highlighted
 * bundles, or a single-colour listing such as the black or the 613 bundles,
 * whichever sells the chosen shade) and ONE closure or frontal, taken from a
 * list of closure products
 * (5x5 closure, 2x6 closure, 13x4 frontal, 13x6 frontal ...). Each pick
 * resolves to its own real variant and is charged at that variant's price —
 * the set total is the plain sum. There is no set discount.
 *
 * The lines share a hidden _combo_id, never _bundle_id: the bundle-tier-discount
 * Function only reads _bundle_id (and the legacy _hq_bundle), so a combo can
 * never pick up the multi-bundle tier by accident. If a set discount is ever
 * wanted, it has to be added to the Function first (grouping on _combo_id),
 * and priceSet() below given the same percentage — page and checkout must
 * agree to the cent, as they do for the bundle sets.
 *
 * All money is integer cents.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.HqComboLogic = factory();
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /**
   * One spelling for an option value across listings. "Champagne-Blonde-Blend",
   * "Champagne Blonde Blend" and "Champagne Blonde" are the same shade; "Lux
   * Deep Wave" and "⁠Lux deep wave" (a hidden word joiner, as typed on one
   * listing) are the same texture. Case, spacing, punctuation, invisible
   * characters and the word "blend" are dropped.
   */
  function key(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .split('-')
      .filter(function (w) { return w && w !== 'blend'; })
      .join('-');
  }

  /** "16\"" / "16 in" / "16" -> 16; 0 when there is no number. */
  function inches(value) {
    var m = String(value == null ? '' : value).match(/\d+/);
    return m ? parseInt(m[0], 10) : 0;
  }

  /** Lengths in ascending inches, original spellings kept. */
  function sortLengths(lengths) {
    return (lengths || []).slice().sort(function (a, b) { return inches(a) - inches(b); });
  }

  /** The value in `values` that means the same as `wanted` (see key), or null. */
  function sameValue(values, wanted) {
    var k = key(wanted);
    if (!k) return null;
    for (var i = 0; i < (values || []).length; i++) if (key(values[i]) === k) return values[i];
    return null;
  }

  /* ------------------------------------------------------------ bundle lengths

     Free choice, the way the shopper reads it: tap a length to add a bundle of
     it, tap it again for a second one. `picks` is in tap order, so "the most
     recent" is always the last item. Nothing is pre-filled. */

  /**
   * A tap on `length`.
   * - Room left: one more bundle of it.
   * - Set full: it replaces the OLDEST pick, first in first out. Picking
   *   14, 16, 18 and then tapping 20, 22, 24 walks the set to 16-18-20,
   *   18-20-22, 20-22-24 — each new tap takes the place of the one chosen
   *   longest ago. A length already in the set counts the same, so tapping 16
   *   on a full 14-16-18 gives 16-16-18. Taking a bundle out is the chip's ✕
   *   (removeLength), not a tap here.
   */
  function tapLength(picks, length, count) {
    var out = (picks || []).slice();
    var n = Math.max(1, count | 0);
    while (out.length >= n) out.shift();
    out.push(length);
    return out;
  }

  /**
   * Every shade the page offers, across its bundle listings, in listing order.
   * A listing with a colour option offers each of its values; a single-colour
   * listing (the black bundles, the 613 bundles) offers the one shade it is
   * given a name for. Each shade remembers which listing sells it.
   *
   * @param {{color: number, options: string[][], shade?: string}[]} bundles
   * @returns {{id: string, value: string, product: number}[]}
   */
  function bundleShades(bundles) {
    var out = [];
    var seen = {};
    (bundles || []).forEach(function (b, i) {
      var values = b.color > -1 ? (b.options[b.color] || []) : (b.shade ? [b.shade] : []);
      values.forEach(function (v) {
        var k = key(v);
        if (!k || seen[k]) return;
        seen[k] = true;
        out.push({ id: i + ':' + v, value: v, product: i });
      });
    });
    return out;
  }

  /**
   * The picks carried over to another listing's lengths: each one becomes the
   * length there with the same inches ('16"' and '16in' are the same length),
   * and one the other listing does not sell is dropped.
   */
  function carryLengths(picks, targetLengths) {
    var out = [];
    (picks || []).forEach(function (p) {
      var inch = inches(p);
      for (var i = 0; i < (targetLengths || []).length; i++) {
        if (inches(targetLengths[i]) === inch) { out.push(targetLengths[i]); return; }
      }
    });
    return out;
  }

  /** One bundle of `length` out (the most recently added one). */
  function removeLength(picks, length) {
    var out = (picks || []).slice();
    var at = out.lastIndexOf(length);
    if (at > -1) out.splice(at, 1);
    return out;
  }

  /** A smaller set keeps the earliest picks; a larger one keeps them all. */
  function fitToCount(picks, count) {
    return (picks || []).slice(0, Math.max(1, count | 0));
  }

  function countOf(picks, length) {
    return (picks || []).filter(function (x) { return x === length; }).length;
  }

  /* ----------------------------------------------- bundles with their own shade

     HQ-MIX (2026-11-03, the owner's request: "choose the first bundle in 1B,
     the second in 27, the third a whole different colour", as the invisible
     clip-ins do it). A pick is { len, shade }: shade null means the bundle
     follows the set's shade above; a shade id gives it its own. Tap order is
     kept, as with plain lengths, so the rules below are the same ones:
     first in first out when full, the latest of a length out first. */

  function makePick(len, shade) { return { len: len, shade: shade == null ? null : shade }; }

  /** tapLength for picks: a new bundle of `length` following the set's shade. */
  function tapPick(picks, length, count) {
    var out = (picks || []).slice();
    var n = Math.max(1, count | 0);
    while (out.length >= n) out.shift();
    out.push(makePick(length, null));
    return out;
  }

  /** One pick out by its place in tap order. */
  function removePickAt(picks, index) {
    var out = (picks || []).slice();
    if (index > -1 && index < out.length) out.splice(index, 1);
    return out;
  }

  /** Places in tap order, shortest length first (ties keep tap order). */
  function pickOrder(picks) {
    return (picks || []).map(function (p, i) { return i; }).sort(function (a, b) {
      return (inches(picks[a].len) - inches(picks[b].len)) || (a - b);
    });
  }

  function pickLengths(picks) { return (picks || []).map(function (p) { return p.len; }); }

  /** Does any bundle have a shade of its own that is not the set's? */
  function mixed(picks, setShade) {
    return (picks || []).some(function (p) { return p.shade != null && p.shade !== setShade; });
  }

  /* ---------------------------------------------------------------- closures */

  /** "5×5 HD Lace Closure" -> "5×5 Closure", "13x4 HD Lace Frontal" -> "13x4 Frontal". */
  function shortTitle(title) {
    return String(title || '')
      .replace(/\b(HD|Lace)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * The closure choices offered, in the order the products are listed. A
   * product with a size option ("Closure Size": 2X6, 6X6) offers one choice per
   * size; any other product is one choice.
   *
   * @param {{title: string, size: number, options: string[][]}[]} products
   * @returns {{id: string, product: number, size: (string|null), label: string}[]}
   */
  function closureTypes(products) {
    var out = [];
    (products || []).forEach(function (p, i) {
      var name = shortTitle(p.title);
      if (p.size > -1 && p.options[p.size] && p.options[p.size].length) {
        p.options[p.size].forEach(function (sz) {
          out.push({ id: i + ':' + sz, product: i, size: sz, label: (String(sz).toLowerCase() + ' ' + name).trim() });
        });
      } else {
        out.push({ id: String(i), product: i, size: null, label: name });
      }
    });
    return out;
  }

  /**
   * The closure length for a set: `preferred` when this closure comes in it
   * (the shopper chose it), else the shortest bundle's length, else the
   * nearest length this closure has (the shorter of two equally near).
   */
  function closureLengthFor(bundleLengths, closureLengths, preferred) {
    var options = sortLengths(closureLengths);
    if (!options.length) return null;
    if (preferred && options.indexOf(preferred) > -1) return preferred;
    var target = preferred ? inches(preferred) : Math.min.apply(null, (bundleLengths || []).map(inches).filter(Boolean));
    if (!isFinite(target) || !target) return options[0];
    var best = options[0];
    var bestGap = Math.abs(inches(best) - target);
    options.forEach(function (o) {
      var gap = Math.abs(inches(o) - target);
      if (gap < bestGap) { best = o; bestGap = gap; }
    });
    return best;
  }

  /**
   * The variant whose options include every wanted value. `want` maps an
   * option index to a value; variants are { id, o: [values], p, a }. Values
   * are compared by key(), so two listings that spell a shade or a texture
   * differently still meet.
   */
  function findVariant(variants, want) {
    var idx = Object.keys(want);
    var keys = idx.map(function (i) { return key(want[i]); });
    for (var v = 0; v < (variants || []).length; v++) {
      var o = variants[v].o;
      var ok = true;
      for (var j = 0; j < idx.length; j++) {
        if (key(o[idx[j]]) !== keys[j]) { ok = false; break; }
      }
      if (ok) return variants[v];
    }
    return null;
  }

  /** Plain sum. Returns null if any part is missing. */
  function priceSet(bundleVariants, closureVariant) {
    if (!closureVariant || !bundleVariants || !bundleVariants.length || bundleVariants.some(function (v) { return !v; })) return null;
    var bundles = bundleVariants.reduce(function (s, v) { return s + v.p; }, 0);
    return { bundles: bundles, closure: closureVariant.p, total: bundles + closureVariant.p };
  }

  /**
   * The price on show while the set is still being chosen, so there is always
   * a number at the bottom of the screen: the bundles picked so far at their
   * own prices, each bundle still to choose at the cheapest length on offer,
   * and the closure as it stands. It is the least the set can cost from here,
   * shown as "From $X"; `exact` once nothing is left to choose, and then it is
   * priceSet's total to the cent. Null while a part is unknown (loading, or no
   * length on offer).
   */
  function priceEstimate(pickedVariants, left, cheapest, closureVariant) {
    var picked = pickedVariants || [];
    var n = Math.max(0, left | 0);
    if (!closureVariant || picked.some(function (v) { return !v; })) return null;
    if (n && cheapest == null) return null;
    var bundles = picked.reduce(function (s, v) { return s + v.p; }, 0) + n * cheapest;
    return { total: bundles + closureVariant.p, exact: n === 0 };
  }

  /**
   * The summary's shipping line. 'free' only where the store's free rate
   * certainly applies: a US shopper paying in the shop's currency, at or over
   * the free-shipping minimum (Settings → Shipping and delivery: "FREE
   * Shipping", US only, order total $150 and up — the block's setting must
   * match it). Everyone else is told it is worked out at checkout, which is
   * true for every rate the store has. All money in cents.
   *
   * @param {number|null} total
   * @param {{min: number, country: string, currency: string, shopCurrency: string}} o
   * @returns {'free'|'checkout'}
   */
  function shippingLine(total, o) {
    var ok = total != null && o && o.min > 0 && o.country === 'US' &&
      !!o.currency && o.currency === o.shopCurrency && total >= o.min;
    return ok ? 'free' : 'checkout';
  }

  /** Unique per add. Only a grouping key for the cart. */
  function makeComboId(now, random) {
    var t = (now == null ? Date.now() : now).toString(36);
    var r = (random == null ? Math.random() : random).toString(36).slice(2, 8);
    return 'HQC-' + t + '-' + r;
  }

  /**
   * The /cart/add.js items for one set. Identical bundles collapse into one
   * line with a quantity (Shopify merges same-variant same-property lines
   * anyway). Every line carries the set's _combo_id and its visible "Set"
   * label. `texture` is written on the closure line only when the closure
   * listing has no texture option of its own, so whoever packs the order sees
   * which texture it is meant to match.
   *
   * @param {{bundleIds: number[], closureId: number, comboId: string, label: string, texture?: string}} opts
   */
  function buildCartItems(opts) {
    var items = [];
    var byId = {};
    opts.bundleIds.forEach(function (id) {
      if (byId[id]) { byId[id].quantity += 1; return; }
      byId[id] = { id: id, quantity: 1, properties: { _combo_id: opts.comboId, Set: opts.label } };
      items.push(byId[id]);
    });
    var closureProps = { _combo_id: opts.comboId, Set: opts.label };
    if (opts.texture) closureProps.Texture = opts.texture;
    items.push({ id: opts.closureId, quantity: 1, properties: closureProps });
    return items;
  }

  return {
    key: key,
    shadeKey: key,
    inches: inches,
    sortLengths: sortLengths,
    sameValue: sameValue,
    bundleShades: bundleShades,
    carryLengths: carryLengths,
    tapLength: tapLength,
    removeLength: removeLength,
    fitToCount: fitToCount,
    countOf: countOf,
    makePick: makePick,
    tapPick: tapPick,
    removePickAt: removePickAt,
    pickOrder: pickOrder,
    pickLengths: pickLengths,
    mixed: mixed,
    shortTitle: shortTitle,
    closureTypes: closureTypes,
    closureLengthFor: closureLengthFor,
    findVariant: findVariant,
    priceSet: priceSet,
    priceEstimate: priceEstimate,
    shippingLine: shippingLine,
    makeComboId: makeComboId,
    buildCartItems: buildCartItems,
  };
}));
